/**
 * Task lifecycle action client — 对齐新 spec
 * `docs/superpowers/specs/2026-05-10-task-core-redesign.md` §7.3 §7.4 §7.6.
 *
 * 全部 mutation 走 action-style 端点:
 *   POST /pms/tasks/{id}/actions/<verb>
 *
 * 12 个单条 action endpoint(spec §7.3) + 1 个 sugar helper(`discard`):
 *
 *   ─ Time tracking(返回更新后的 Task)─────────────────────────
 *   - start    body { notes? }
 *   - pause    body { notes? }
 *   - resume   body { notes? }
 *
 *   ─ Resolution-bearing(返回 ResolutionActionResponse)────────
 *   - finish   body { reason_code?, job_result_note?, output? }
 *              special: 必须不带 reason_code(否则 422)
 *              on-site/admin: reason_code 必填(否则 422)
 *   - hold     body { reason_code, job_result_note?, output? }
 *   - cancel   body { reason_code, job_result_note?, output? }
 *
 *   2026-05-28 后端 P1: wire 字段从 `notes` rename 为 `job_result_note`(对齐
 *   `task.job_result_note` 列). 后端短期 alias='notes' 兼容旧 FE,但 1-2 release
 *   后废,前端已先迁移. FE 内部 `TaskFinishPayload.notes` 等 type 字段名保留
 *   (caller 不改),service 层只在落 wire 时换名.
 *
 *   FE plan §4.7 / backend Phase 4 — `suggested_next_action` field 已退役;
 *   不再传(否则 backend `extra='forbid'` 会 422 INVALID_PAYLOAD).
 *
 *   ─ Pure state-change(返回更新后的 Task)────────────────────
 *   - unhold     body {}                       (2026-05-28: `notes` 字段已删,传则后端 422)
 *   - reopen     body { reason?, target_status?, assignee_id?, planned_start_at? }  (2026-05-28: `notes` 字段已删)
 *   - close      body { reason? }              (2026-05-28: `notes` 字段已删)
 *   - reassign   body { assignee_id: string|null, reason?, note? }    (caller passes `notes` plural — service maps to wire `note`)
 *   - reschedule body { planned_start_at?, due_at?, note? }            (caller passes `notes` plural — service maps to wire `note`)
 *
 *   ─ Co-worker ───────────────────────────────────────────────
 *   - inviteHelper   body { helper_user_id, title?, notes? }  → TaskInviteHelperResponse
 *   - withdrawHelper body { notes? }                          → 更新后的 child Task
 *     (id 传 child task,撤销协助邀请 → child 置 cancelled;见方法注释)
 *
 *   ─ Sugar helper(无新 endpoint)────────────────────────────
 *   - discard    转发到 cancel,reason_code='created_in_error'
 *
 * 错误映射:每个 call try/catch,有 response.data 时通过 parseApiError 映射成
 * typed Error subclass(`InProgressReassignBlockedError` /
 * `TaskClosedLockedError` 等);UI 层用 `instanceof` 判断。
 *
 * 不附带 `Idempotency-Key` header(spec §7.5 — 自然幂等,不暴露 header)。
 *
 * 注意:UI 旧调用站(用 PUT / `note` 单数 / 老 verb 名)会 TS 编译失败,W3-W6
 * 重写 UI 时一起修。
 */
import axios from "@/api/axios-config";
import { parseApiError } from "./errors";
import { emitEntityChanged } from "@/lib/entityChangeBus";
import type { Resolution, Task } from "@/types/task";
import type { DispatchPayload } from "@/components/dispatch";

// ──────────────────────────────────────────────────────────────
// Payload 类型(snake_case 与后端 Pydantic schema 一致 — spec §7.6)
// ──────────────────────────────────────────────────────────────

export interface TaskNotesOnlyPayload {
  notes?: string;
}

export interface TaskFinishPayload {
  /**
   * Special task: 必须不传(否则 422 SPECIAL_TASK_NO_RESOLUTION_ALLOWED)。
   * On-site / admin: 必填(否则 422 RESOLUTION_REASON_REQUIRED);
   * 须在 `pms.task_resolution_reason` catalog 中存在,且 applicable_status='completed'
   * + applicable_to overlap task.task_type_code/task_subtype_code。
   */
  reason_code?: string;
  notes?: string;
  /** Subtype-defined output form values (wire field `output`, camelCase). */
  output?: Record<string, any>;
}

export interface TaskHoldPayload {
  /** 必填;catalog applicable_status='on_hold' + applicable_to overlap。 */
  reason_code: string;
  notes?: string;
  /** Subtype-defined output form values (wire field `output`, camelCase). */
  output?: Record<string, any>;
}

export interface TaskCancelPayload {
  /** 必填;catalog applicable_status='cancelled' + applicable_to overlap;admin discard 用 'created_in_error'。 */
  reason_code: string;
  notes?: string;
  /** Subtype-defined output form values (wire field `output`, camelCase). */
  output?: Record<string, any>;
}

// ──────────────────────────────────────────────────────────────
// Response 类型
// ──────────────────────────────────────────────────────────────

/**
 * Resolution-bearing actions(finish on-site/admin、hold、cancel)的响应
 * spec §7.6
 */
export interface ResolutionActionResponse {
  task: Task;
  /**
   * The resolution row created by this action. Wire format is camelCase
   * (backend Pydantic schema uses `alias_generator=to_camel`); the FE
   * `Resolution` type matches 1:1. axios-config.ts does not run any
   * camelizer — `safeJsonParse` only protects Snowflake big-int precision.
   */
  resolution: Resolution;
  /** Task 自身被关闭的 work session id 列表(0..N)。SnowflakeId 字符串。 */
  affected_work_session_ids: string[];
}

/** invite-helper 响应 — spec §7.6。 */
export interface TaskInviteHelperResponse {
  /** 父任务(unchanged,只 echo)。 */
  parent_task: Task;
  /** 新建的 child support task。 */
  child_task: Task;
}

// ──────────────────────────────────────────────────────────────
// Internal helpers
// ──────────────────────────────────────────────────────────────

async function executeAction<TResp>(
  taskId: string,
  verb: string,
  body: Record<string, unknown>,
): Promise<TResp> {
  try {
    const data = (await axios.post(
      `/pms/tasks/${taskId}/actions/${verb}`,
      body,
    )) as unknown as TResp;
    // 脏标记广播 —— 所有单任务动作(finish/hold/cancel/close/reopen/dispatch/
    // reschedule/transition-stage/…)都从这里出去,埋在收口处调用方就不必逐个
    // 记得通知别的视图(见 lib/entityChangeBus)。
    emitEntityChanged({ entity: "task", id: taskId });
    return data;
  } catch (err: unknown) {
    const response = (err as { response?: { data?: unknown; status?: number } })?.response;
    if (response?.data) {
      throw parseApiError(response.status ?? 0, response.data);
    }
    throw err;
  }
}

// 仅保留有显式赋值(非 undefined)的字段。null 是合法值(显式清空 / 卸载),
// 不能在这里被过滤。
function compact(body: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(body)) {
    if (v !== undefined) out[k] = v;
  }
  return out;
}

// ──────────────────────────────────────────────────────────────
// Public API — taskActions
// 注意:导出名是 `taskActions`(不是 `taskActionsService`),保持与
// 现有 20+ 调用站点一致。文件名仍是 taskActionsService.ts。
// ──────────────────────────────────────────────────────────────

export const taskActions = {
  // ─ Time tracking ─────────────────────────────────────────
  start(taskId: string, body: TaskNotesOnlyPayload = {}): Promise<Task> {
    return executeAction<Task>(taskId, "start", compact({ notes: body.notes }));
  },

  pause(taskId: string, body: TaskNotesOnlyPayload = {}): Promise<Task> {
    return executeAction<Task>(taskId, "pause", compact({ notes: body.notes }));
  },

  resume(taskId: string, body: TaskNotesOnlyPayload = {}): Promise<Task> {
    return executeAction<Task>(taskId, "resume", compact({ notes: body.notes }));
  },

  // ─ Resolution-bearing ───────────────────────────────────
  // 2026-05-28: wire 字段 `notes` → `job_result_note`(后端列名对齐). FE
  // payload type 内字段保留 `notes`(caller 不改),service 层做名字映射.
  finish(
    taskId: string,
    body: TaskFinishPayload,
  ): Promise<ResolutionActionResponse> {
    return executeAction<ResolutionActionResponse>(
      taskId,
      "finish",
      compact({
        reason_code: body.reason_code,
        job_result_note: body.notes,
        output: body.output,
      }),
    );
  },

  hold(
    taskId: string,
    body: TaskHoldPayload,
  ): Promise<ResolutionActionResponse> {
    return executeAction<ResolutionActionResponse>(
      taskId,
      "hold",
      compact({
        reason_code: body.reason_code,
        job_result_note: body.notes,
        output: body.output,
      }),
    );
  },

  cancel(
    taskId: string,
    body: TaskCancelPayload,
  ): Promise<ResolutionActionResponse> {
    return executeAction<ResolutionActionResponse>(
      taskId,
      "cancel",
      compact({
        reason_code: body.reason_code,
        job_result_note: body.notes,
        output: body.output,
      }),
    );
  },

  // ─ Pure state-change ────────────────────────────────────
  // 2026-05-28 backend breaking change: unhold/reopen/close 三个 endpoint 已删除
  // `notes` 字段(后端 schema `extra='forbid'` 会 422)。若要记录解除/重开/关闭
  // 原因,reopen/close 用 `reason` 自由文本;unhold 没有备注通道。
  unhold(taskId: string): Promise<Task> {
    return executeAction<Task>(taskId, "unhold", {});
  },

  reopen(
    taskId: string,
    body: { reason?: string; target_status?: string; assignee_id?: string; planned_start_at?: string } = {},
  ): Promise<Task> {
    // 2026-10-27: Quick create「没修好，重新打开并派给某人」—— 同一次调用里带人和时间
    // (assignee_id / planned_start_at,snake_case)。没带就不发,旧调用方不变。
    // 2026-06-06 two-axis decouple (§2): `target_status` lets Reactivate land the
    // task on `on_hold` (instead of the default `open`) so it re-enters a triage
    // queue without flicker. Compacted out when omitted → legacy callers (plain
    // reopen→open) unchanged. Requires the backend decouple change to honor it.
    return executeAction<Task>(
      taskId,
      "reopen",
      compact({
        reason: body.reason,
        target_status: body.target_status,
        assignee_id: body.assignee_id,
        planned_start_at: body.planned_start_at,
      }),
    );
  },

  close(
    taskId: string,
    body: { reason?: string } = {},
  ): Promise<Task> {
    return executeAction<Task>(
      taskId,
      "close",
      compact({ reason: body.reason }),
    );
  },

  reassign(
    taskId: string,
    body: {
      assignee_id: string | null;
      reason?: string;
      notes?: string;
      /**
       * 改派时已领取(picked_up)物料的**默认**去向。不传 = 后端默认 true(跟着
       * 任务走)。物料不跟着走的话,新执行人完成任务时会因自己车上没料而被卡死。
       * 逐条控制见 material_actions —— 该字段只对未逐条指定的行生效。
       */
      transfer_materials?: boolean;
      /**
       * 逐条指定每条已领物料的去向:transfer=跟着转 / keep=留在原执行人 /
       * return=退回当初领料的仓库(行回到待领取)。未列出的行按
       * transfer_materials 兜底。
       */
      material_actions?: Array<{
        expense_id: string;
        action: "transfer" | "keep" | "return";
      }>;
    },
  ): Promise<Task> {
    // assignee_id 是必填字段(可为 null),不能用 compact 过滤。
    // wire 字段名 = `notes`(plural), 与后端 task_actions_schema.TaskReassignPayload
    // / service / mobile 一致 (历史 bug: 曾误发 `note` 单数被 422)。
    return executeAction<Task>(taskId, "reassign", {
      assignee_id: body.assignee_id,
      ...compact({
        reason: body.reason,
        notes: body.notes,
        transfer_materials: body.transfer_materials,
        material_actions: body.material_actions,
      }),
    });
  },

  reschedule(
    taskId: string,
    body: {
      planned_start_at?: string | null;
      due_at?: string | null;
      notes?: string;
      reason?: string;
    },
  ): Promise<Task> {
    // planned_start_at / due_at 的 null 是显式清空,必须保留;只过滤 undefined。
    // wire 字段 `notes`(plural), 与后端 task_actions_schema.TaskReschedulePayload
    // / service / mobile 一致 (历史 bug: 曾误发 `note` 单数被 422)。
    return executeAction<Task>(
      taskId,
      "reschedule",
      compact({
        planned_start_at: body.planned_start_at,
        due_at: body.due_at,
        notes: body.notes,
        reason: body.reason,
      }),
    );
  },

  /**
   * 原子 dispatch — 合并 reschedule + reassign + supervisor + duration。
   * 端点 POST /pms/tasks/{id}/actions/dispatch（spec 2026-06-19 §4.5）。
   * 三态：null 是显式清空（unassign / 清排期）必须保留；undefined 是"没改"过滤。
   */
  dispatch(taskId: string, body: DispatchPayload): Promise<Task> {
    return executeAction<Task>(taskId, "dispatch", compact({ ...body }));
  },

  // ─ Co-worker ────────────────────────────────────────────
  inviteHelper(
    taskId: string,
    body: { helper_user_id: string; title?: string; notes?: string },
  ): Promise<TaskInviteHelperResponse> {
    return executeAction<TaskInviteHelperResponse>(
      taskId,
      "invite-helper",
      {
        helper_user_id: body.helper_user_id,
        ...compact({ title: body.title, notes: body.notes }),
      },
    );
  },

  /**
   * 撤销协助邀请 — POST /pms/tasks/{childTaskId}/actions/withdraw-helper。
   *
   * ⚠️ `childTaskId` 是 **child(helper)task 的 id**,不是父任务。
   *
   * 为什么需要这个出口:helper child 是 special 类型,普通 cancel/hold 对它会被
   * 422 SPECIAL_TASK_INVALID_OPERATION 拒;`finish_special` 又要求 in_progress。
   * 所以"从未 start 的 helper child"卡住父任务 finish/cancel/close 时
   * (409 CHILD_TASKS_NOT_RESOLVED),唯一解锁手段就是本 action。
   *
   * 授权(与 invite-helper 对称,后端 task_actions_controller.py
   * `withdraw_helper_action`):调用者须是 **父任务** 的 primary assignee /
   * supervisor / superuser,否则 403 NOT_PRIMARY_ASSIGNEE。child 自己的 assignee
   * (helper)无权走此入口。
   *
   * 副作用:关掉 child 的活跃 session(若 in_progress)→ child.status='cancelled'
   * (special 不写 resolution,I10)→ 发 task.helper_withdrawn 事件。
   * 已 cancelled 幂等 no-op;completed 不可撤(409);closed 403。
   *
   * 返回:更新后的 **child** Task(后端 response_model=TaskOutput)。
   */
  withdrawHelper(
    childTaskId: string,
    body: { notes?: string } = {},
  ): Promise<Task> {
    return executeAction<Task>(
      childTaskId,
      "withdraw-helper",
      compact({ notes: body.notes }),
    );
  },

  // ─ Workflow stage transition ────────────────────────────
  /**
   * 推进 / 退出 workflow stage(Triage 拖拽)。
   * 端点 POST /pms/tasks/{id}/actions/transition-stage。
   *
   * ⚠️ 字段大小写例外:本文件其他 action 用 snake_case(reason_code 等),
   * 但 transition-stage 复用后端 `TaskFollowupTransitionPayload` schema,
   * 字段是 camelCase(`fromStage` / `toStage` / `notes`)。已对照
   * main-api(task_actions_schema.py + task_actions_controller.py 路由
   * /{task_id}/actions/transition-stage)确认 —— 这里发 camelCase,不要 snake。
   *
   * - fromStage 是乐观并发:必须 === task 当前 wf_stage(含 null),
   *   否则后端 422 STAGE_MISMATCH。
   * - toStage null = 退出 stage;否则须 ∈ workflow_entity_stage(active),
   *   不然 422。
   * - 返回完整更新后的 Task(后端 response_model=TaskOutput)。
   * - compact 只剥 undefined,保留 null —— fromStage/toStage 的 null 透传(退出语义)。
   */
  transitionStage(
    taskId: string,
    body: { fromStage: string | null; toStage: string | null; notes?: string },
  ): Promise<Task> {
    return executeAction<Task>(
      taskId,
      "transition-stage",
      compact({
        fromStage: body.fromStage,
        toStage: body.toStage,
        notes: body.notes,
      }),
    );
  },

  // ─ Sugar helper(no new endpoint)────────────────────────
  /**
   * Admin discard — 转发到 `cancel`,reason_code 固定为 'created_in_error'。
   * 设计意图:页面上 "Discard" 按钮(误创建任务清理)= 一次 cancel,
   * UI 不必每次填 reason picker。spec §7.3 cancel 段。
   */
  discard(taskId: string, notes?: string): Promise<ResolutionActionResponse> {
    return taskActions.cancel(taskId, {
      reason_code: "created_in_error",
      ...compact({ notes }),
    });
  },
};
